import { useEffect, useRef } from 'react';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { toast } from 'sonner';

import { cn } from '@/lib/utils/cn';

import { SPEED_KBPS, formatKbps, useNetworkSpeed, type SpeedLevel } from './useNetworkSpeed';

const TEXT: Record<SpeedLevel, string> = {
  good: 'text-emerald-600 dark:text-emerald-400',
  moderate: 'text-orange-500 dark:text-orange-400',
  poor: 'text-red-600 dark:text-red-400',
  unknown: 'text-muted-foreground',
};

const RING: Record<SpeedLevel, string> = {
  good: 'ring-emerald-500/30',
  moderate: 'ring-orange-500/50',
  poor: 'ring-red-500/60',
  unknown: 'ring-transparent',
};

const LABEL: Record<SpeedLevel, string> = {
  good: 'Good',
  moderate: 'Moderate',
  poor: 'Poor',
  unknown: 'Checking…',
};

const SEVERITY: Record<SpeedLevel, number> = { unknown: 0, good: 0, moderate: 1, poor: 2 };

/**
 * Compact upload / download speed for the agent and team-lead header (next to the bell).
 * Green = good, orange = moderate, red = poor for a voice call. When the line gets worse
 * (good → moderate, or → poor) a popup tells the user; it shows again only after the
 * speed recovers and drops once more.
 */
export default function NetworkSpeedIndicator() {
  const speed = useNetworkSpeed();
  const shownSeverity = useRef(0);

  useEffect(() => {
    const severity = SEVERITY[speed.level];
    if (severity === 0) {
      if (speed.level === 'good') shownSeverity.current = 0;
      return;
    }
    if (severity <= shownSeverity.current) return;
    shownSeverity.current = severity;

    const detail = speed.offline
      ? 'You are offline. Calls will not connect until the internet is back.'
      : `Upload ${formatKbps(speed.upKbps)} · Download ${formatKbps(speed.downKbps)}. ` +
        `A call needs at least ${SPEED_KBPS.goodFrom} kbps each way, so callers may not hear you clearly.`;
    const show = speed.level === 'poor' ? toast.error : toast.warning;
    show(speed.level === 'poor' ? 'Internet speed is very low' : 'Internet speed is getting low', {
      id: 'network-speed',
      description: detail,
      duration: 10_000,
    });
  }, [speed.level, speed.offline, speed.upKbps, speed.downKbps]);

  // First reading not in yet.
  const checking = !speed.offline && speed.measuredAt === null;

  const title = speed.offline
    ? 'Internet: offline'
    : `Internet: ${LABEL[speed.level]}\nUpload ${formatKbps(speed.upKbps)} (${LABEL[speed.up]})\nDownload ${formatKbps(speed.downKbps)} (${LABEL[speed.down]})\nGood ≥ ${SPEED_KBPS.goodFrom} kbps · Moderate ${SPEED_KBPS.moderateFrom}–${SPEED_KBPS.goodFrom} kbps · Poor < ${SPEED_KBPS.moderateFrom} kbps`;

  return (
    <div
      title={title}
      aria-label={title.replace(/\n/g, '. ')}
      className={cn(
        'flex h-7 shrink-0 flex-col justify-center gap-px rounded-2xl bg-card px-2 leading-none shadow-neu-raised ring-1 ring-inset',
        RING[speed.level],
      )}
    >
      <span className={cn('flex items-center gap-0.5 text-[10px] font-semibold tabular-nums', TEXT[speed.offline ? 'poor' : speed.up])}>
        <ArrowUp size={10} strokeWidth={2.5} />
        {speed.offline ? 'Offline' : checking ? '…' : formatKbps(speed.upKbps)}
      </span>
      <span className={cn('flex items-center gap-0.5 text-[10px] font-semibold tabular-nums', TEXT[speed.offline ? 'poor' : speed.down])}>
        <ArrowDown size={10} strokeWidth={2.5} />
        {speed.offline ? '—' : checking ? '…' : formatKbps(speed.downKbps)}
      </span>
    </div>
  );
}
